import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Briefcase, Car, Loader2, Users } from "lucide-react";
import api, { PLATFORM_NAME, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InstallPrompt } from "@/components/InstallPrompt";
import { PasskeyUnlock } from "@/components/PasskeyUnlock";
import { CompanyFinder } from "@/components/CompanyFinder";
import { WrongDoor } from "@/components/WrongDoor";

// The three doors. Same backend, three separate front doors — each installable on its own.
// A passkey is bound to the whole domain, but the challenge records which door was used.
const PASSKEY_SCOPE = { clients: "client", drivers: "driver", office: "office" };

export const DOORS = {
  clients: {
    key: "clients",
    path: "/portal/client/login",
    icon: Users,
    eyebrow: "Passenger app",
    title: "Your car, on your terms",
    blurb: "Book a chauffeur, watch them arrive on the map, message them, add a stop mid-trip and settle up — no phone calls.",
    accent: "text-gold",
    roles: ["client"],
    home: "/portal/client",
    points: ["Instant quote before you book", "Live chauffeur tracking", "Message your chauffeur",
             "Add a stop mid-trip", "Receipt, rating and tip"],
    cta: "Sign in to your trips",
  },
  drivers: {
    key: "drivers",
    path: "/portal/driver/login",
    icon: Car,
    eyebrow: "Chauffeur app",
    title: "Every run, in your pocket",
    blurb: "Today's runs, one-tap status updates, airport manifests you can fill in yourself, and what you've earned.",
    accent: "text-emerald-300",
    roles: ["driver"],
    home: "/portal/driver",
    points: ["Today's assigned runs", "One-tap trip status", "Airport manifests & PDF",
             "Wait timers at every stop", "Monthly earnings statement"],
    cta: "Sign in to your runs",
  },
  office: {
    key: "office",
    path: "/app/login",
    icon: Briefcase,
    eyebrow: "Dispatch & owner app",
    title: "Run the whole floor",
    blurb: "Dispatch board, trips, pricing, airport manifests, invoices, chauffeur payouts and your affiliate network.",
    accent: "text-sky-300",
    roles: ["owner", "manager", "dispatcher", "accountant"],
    home: "/app",
    points: ["Live dispatch board", "Flexible pricing engine", "Invoices & payments",
             "Chauffeur payouts & 1099", "Affiliate farm-in / farm-out"],
    cta: "Sign in to dispatch",
  },
};

export default function Door({ door }) {
  const config = DOORS[door];
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const Icon = config.icon;

  useEffect(() => {
    if (!user) return;
    if (config.roles.includes(user.role)) navigate(config.home, { replace: true });
    // If it is NOT their door we stay put and render WrongDoor below. Navigating to the other app's
    // home would make an installed PWA (its own scope) show a different app entirely.
  }, [user, config, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const data = await login({ email: form.email, password: form.password });
      const role = data.user?.role;
      if (!config.roles.includes(role)) {
        setBusy(false);
        return; // WrongDoor takes over and offers a clean sign-out.
      }
      navigate(config.home, { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (user && !config.roles.includes(user.role)) {
    return <WrongDoor expected={config.key === "clients" ? "client" : config.key === "drivers" ? "driver" : "office"} />;
  }

  return (
    <div data-testid={`door-${config.key}`} className="min-h-screen bg-ink-900 text-slate-200">
      <div className="mx-auto grid max-w-6xl gap-12 px-6 py-16 lg:grid-cols-2 lg:py-24">
        <div>
          <Link to="/" className="flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500 hover:text-gold">
            <img src="/limolinkx-logo.png" alt="" className="h-8 w-8 rounded bg-white/95 object-contain p-0.5" />
            {PLATFORM_NAME}
          </Link>
          <p className={`mt-10 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.28em] ${config.accent}`}>
            <Icon className="h-4 w-4" /> {config.eyebrow}
          </p>
          <h1 className="mt-5 text-4xl font-bold tracking-tight text-slate-50 sm:text-5xl">{config.title}</h1>
          <p className="mt-5 max-w-lg text-base text-slate-400">{config.blurb}</p>
          <ul className="mt-10 space-y-3">
            {config.points.map((p) => (
              <li key={p} className="flex items-center gap-3 text-sm text-slate-300">
                <ArrowRight className={`h-3.5 w-3.5 shrink-0 ${config.accent}`} /> {p}
              </li>
            ))}
          </ul>
          <div className="mt-10 max-w-md">
            <InstallPrompt companyName={`the ${config.eyebrow.toLowerCase()}`}
              blurb={`Install the ${config.eyebrow.toLowerCase()} on this device — it opens full screen from your home screen, no browser bar, and remembers your sign-in.`} />
          </div>
        </div>

        <div className="lg:pt-24">
          <form onSubmit={submit} data-testid={`door-${config.key}-form`}
            className="space-y-5 rounded-2xl border border-ink-600 bg-ink-800/70 p-8">
            <p className="font-mono text-[11px] uppercase tracking-widest text-gold">{config.cta}</p>
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Email</label>
              <Input data-testid={`door-${config.key}-email`} type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="h-11 border-ink-600 bg-ink-900 text-slate-100 focus-visible:ring-gold" />
            </div>
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Password</label>
              <Input data-testid={`door-${config.key}-password`} type="password" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="h-11 border-ink-600 bg-ink-900 text-slate-100 focus-visible:ring-gold" />
            </div>
            {error && <p data-testid={`door-${config.key}-error`} className="text-sm text-red-400">{error}</p>}
            <Button data-testid={`door-${config.key}-submit`} type="submit" disabled={busy}
              className="h-11 w-full bg-gold text-ink-900 hover:bg-gold-light">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : config.cta}
            </Button>

            <PasskeyUnlock scope={PASSKEY_SCOPE[config.key] || "office"} email={form.email}
              testId={`door-${config.key}-passkey`} />
            <p className="text-center text-xs text-slate-500">
              <Link to="/forgot-password" data-testid={`door-${config.key}-forgot`} className="text-gold">
                Forgot your password?
              </Link>
            </p>

            {config.key === "clients" && (
              <p className="text-xs text-slate-500">
                No account? Ask your operator for their booking link — you can book and track without a password.
              </p>
            )}
            {config.key === "office" && (
              <p className="text-xs text-slate-500">
                New here? <Link to="/signup" className="text-gold underline">Start free</Link> — 2 trips a week and
                10 airport manifests a month, no card needed.
              </p>
            )}
          </form>

          {config.key === "clients" && (
            <div className="mt-6">
              <CompanyFinder />
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-4 text-xs text-slate-500">
            {Object.values(DOORS).filter((d) => d.key !== config.key).map((d) => (
              <Link key={d.key} to={d.path} data-testid={`door-link-${d.key}`} className="hover:text-gold">
                {d.eyebrow} →
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
